import { useState } from "react";
import { createClient } from "@supabase/supabase-js";

// ─── Supabase connection ───────────────────────────────────────────────────
const SUPABASE_URL = "https://flnpeqrztmvvkylcqdpw.supabase.co";
const SUPABASE_KEY = "sb_publishable_OPqF6-vkDDFibrsRZwjwAA_oveeYzmx";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─── Helpers ───────────────────────────────────────────────────────────────
function generateTeamCode(orgName) {
  const slug = orgName
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 5);
  const rand = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${slug}-${rand}`;
}

// ─── Data ──────────────────────────────────────────────────────────────────
const ROLES = [
  { id: "executor",  name: "Practitioner",     desc: "You do the work — you're inside the process daily." },
  { id: "designer",  name: "Architect",         desc: "You design, own, or govern how this process is structured." },
  { id: "recipient", name: "Downstream owner",  desc: "You receive the output of this process and depend on it." },
  { id: "governor",  name: "Sponsor",           desc: "You lead, fund, or are accountable for outcomes of this process." },
];

const SECTIONS = [
  {
    id: "s1", section: "How work finds you", sectionNum: 1, total: 6,
    questions: [
      { id: "s1q1", type: "radio", text: "How does most work arrive on your team?", sub: "Pick the one that best describes the dominant pattern.",
        opts: ["Top-down directive — leadership decides and assigns", "A formal request or intake process", "Informal asks — Slack, email, a meeting", "We pull it from a backlog or queue", "It materializes with no clear origin"] },
      { id: "s1q2", type: "tiles", text: "On average, how much advance notice does your team get before new work becomes active?",
        opts: [{ val: "Same day", lbl: "Same day or less" }, { val: "2–4 days", lbl: "A few days" }, { val: "~1 week", lbl: "About a week" }, { val: "2–4 weeks", lbl: "2–4 weeks" }, { val: "1 month+", lbl: "A month or more" }] },
      { id: "s1q3", type: "scale", text: "When new work arrives, how clearly does your team understand the context behind it — the why, the urgency, the outcome expected?", lo: "Almost no context provided", hi: "Always well-contextualized" },
      { id: "s1q4", type: "tiles", text: "In a typical month, roughly how many distinct new work items land on your team?",
        opts: [{ val: "1–5", lbl: "1–5" }, { val: "6–15", lbl: "6–15" }, { val: "16–30", lbl: "16–30" }, { val: "31–50", lbl: "31–50" }, { val: "50+", lbl: "50+" }] },
      { id: "s1q5", type: "open", text: "What's the most common source of confusion or friction when new work comes in?", sub: "Be specific — this is where the real insight lives." },
    ]
  },
  {
    id: "s2", section: "How work gets weighed", sectionNum: 2, total: 6,
    questions: [
      { id: "s2q1", type: "radio", text: "When everything is labeled urgent, what actually determines what gets done first?", sub: "Pick the most honest answer.",
        opts: ["Who escalates the loudest", "Relationships and visibility", "The thing due soonest regardless of importance", "A documented scoring or weighting process", "Whoever has bandwidth picks it up", "It's genuinely inconsistent — it changes each time"] },
      { id: "s2q2", type: "tiles", text: "How many times in the last 30 days did your team's priorities shift significantly?",
        opts: [{ val: "0", lbl: "0" }, { val: "1–2", lbl: "1–2" }, { val: "3–5", lbl: "3–5" }, { val: "6–10", lbl: "6–10" }, { val: "10+", lbl: "10+" }] },
      { id: "s2q3", type: "scale", text: "How well does your team understand what your actual top priority is at any given moment?", lo: "We have no real shared priority order", hi: "Crystal clear to everyone, all the time" },
      { id: "s2q4", type: "scale", text: "How much does your team trust that prioritization decisions are made with accurate, complete information?", lo: "No trust — decisions feel arbitrary", hi: "Complete trust in the process" },
      { id: "s2q5", type: "open", text: "If you could change one thing about how priorities are set on your team, what would it be?" },
    ]
  },
  {
    id: "s3", section: "How work gets to people", sectionNum: 3, total: 6,
    questions: [
      { id: "s3q1", type: "multi", text: "What signals are actually used when deciding who takes on new work?", sub: "Select all that apply.",
        opts: ["Relevant skill or expertise", "Visible bandwidth or availability", "Who's been vocal or present lately", "Seniority or role title", "Whoever asks gets it", "Relationship with leadership", "Honest answer: nobody really knows"] },
      { id: "s3q2", type: "scale", text: "How visible is each person's actual workload to the people making assignment decisions?", lo: "Completely invisible", hi: "Fully visible and consulted" },
      { id: "s3q3", type: "tiles", text: "Right now, what percentage of your team would you say is at or near capacity?",
        opts: [{ val: "0–25%", lbl: "0–25%" }, { val: "25–50%", lbl: "25–50%" }, { val: "50–75%", lbl: "50–75%" }, { val: "75–90%", lbl: "75–90%" }, { val: "90%+", lbl: "90%+" }] },
      { id: "s3q4", type: "radio", text: "When someone is clearly overloaded, what typically happens?",
        opts: ["They push back and it gets resolved quickly", "Work gets redistributed by leadership", "They absorb it and struggle quietly", "Work quietly gets dropped or delayed", "It depends entirely on how vocal they are"] },
      { id: "s3q5", type: "scale", text: "How often does your team lose capacity to burnout, disengagement, or turnover caused by workload imbalance?", lo: "Rarely — workload feels sustainable", hi: "Regularly — people are burning out" },
    ]
  },
  {
    id: "s4", section: "How work actually gets done", sectionNum: 4, total: 6,
    questions: [
      { id: "s4q1", type: "tiles", text: "How many tools does your team use to do their core work?", sub: "Count collaboration, project tracking, communication, documentation, reporting — not personal apps.",
        opts: [{ val: "1–2", lbl: "1–2" }, { val: "3–5", lbl: "3–5" }, { val: "6–8", lbl: "6–8" }, { val: "9–12", lbl: "9–12" }, { val: "12+", lbl: "12+" }] },
      { id: "s4q2", type: "tiles", text: "On a typical workday, how much time is lost to context-switching — moving between unrelated tasks, tools, or conversations?",
        opts: [{ val: "Under 30 min", lbl: "Under 30 min" }, { val: "30–60 min", lbl: "30–60 min" }, { val: "1–2 hours", lbl: "1–2 hours" }, { val: "2–3 hours", lbl: "2–3 hours" }, { val: "3+ hours", lbl: "3+ hours" }] },
      { id: "s4q3", type: "tiles", text: "How many hours per week does your team spend in meetings?", sub: "All meetings — standups, syncs, reviews, 1:1s.",
        opts: [{ val: "Under 3 hrs", lbl: "Under 3 hrs" }, { val: "3–6 hrs", lbl: "3–6 hrs" }, { val: "6–10 hrs", lbl: "6–10 hrs" }, { val: "10–15 hrs", lbl: "10–15 hrs" }, { val: "15+ hrs", lbl: "15+ hrs" }] },
      { id: "s4q4", type: "scale", text: "How much of your team's time goes toward work that directly moves things forward — vs. overhead around the work?", lo: "Almost all overhead, very little forward motion", hi: "Almost all productive output" },
      { id: "s4q5", type: "multi", text: "What are the biggest barriers to doing the work well?", sub: "Select up to 3.",
        opts: ["Unclear requirements or scope", "Too many dependencies on other teams", "Constant interruptions and context-switching", "Information buried across too many tools", "Unclear who has decision authority", "People stretched too thin", "Priorities reset before work completes", "Lack of relevant skills or training"], max: 3 },
      { id: "s4q6", type: "open", text: "Describe the last time a process actively got in the way of doing good work.", sub: "What happened? What did your team have to do around it?" },
    ]
  },
  {
    id: "s5", section: "How teams get support", sectionNum: 5, total: 6,
    questions: [
      { id: "s5q1", type: "scale", text: "When your team hits a real blocker, how easy is it to get the help needed to move forward?", lo: "Nearly impossible — blockers linger", hi: "Straightforward and fast" },
      { id: "s5q2", type: "tiles", text: "On average, how long does it take to resolve a significant blocker once it's flagged?",
        opts: [{ val: "Same day", lbl: "Same day" }, { val: "2–3 days", lbl: "2–3 days" }, { val: "~1 week", lbl: "About a week" }, { val: "2–4 weeks", lbl: "2–4 weeks" }, { val: "1 month+", lbl: "1 month+" }] },
      { id: "s5q3", type: "scale", text: "How safe does your team feel raising problems or flagging risks early — before they become crises?", lo: "Not safe at all — problems get hidden", hi: "Completely safe — early flags are welcomed" },
      { id: "s5q4", type: "scale", text: "When someone raises a problem, how often does something actually change as a result?", lo: "Almost never — it goes nowhere", hi: "Almost always — action follows quickly" },
      { id: "s5q5", type: "open", text: "What kind of support is most missing for your team right now?" },
    ]
  },
  {
    id: "s6", section: "How work gets seen — and by whom", sectionNum: 6, total: 6,
    questions: [
      { id: "s6q1", type: "multi", text: "What formats does leadership most commonly request for updates?", sub: "Select all that apply.",
        opts: ["A slide deck or PowerPoint presentation", "A live dashboard they can check themselves", "A written summary or narrative memo", "A meeting to walk them through it verbally", "A simple RAG status (red/amber/green)", "Raw data in a spreadsheet", "They rarely engage with updates at all"] },
      { id: "s6q2", type: "tiles", text: "How many hours per week does your team spend preparing status updates, reports, and presentations for leadership?",
        opts: [{ val: "Under 1 hr", lbl: "Under 1 hr" }, { val: "1–3 hrs", lbl: "1–3 hrs" }, { val: "3–5 hrs", lbl: "3–5 hrs" }, { val: "5–8 hrs", lbl: "5–8 hrs" }, { val: "8+ hrs", lbl: "8+ hrs" }] },
      { id: "s6q3", type: "radio", text: "When your team brings a new idea or proposes a better way of working, leadership typically:",
        opts: ["Actively encourages experimentation and tries it", "Listens but rarely acts on suggestions", "Tolerates change if it doesn't disrupt their view", "Expects everything to fit their existing mental model", "Signals — explicitly or subtly — not to rock the boat"] },
      { id: "s6q4", type: "scale", text: "How aligned is leadership's understanding of your team's work with the reality your team experiences day-to-day?", lo: "Completely different realities", hi: "We're on the same page" },
      { id: "s6q5", type: "scale", text: "How accurately do your current reports and dashboards reflect what's actually happening on the ground?", lo: "They're mostly theater — polished but hollow", hi: "Highly accurate and genuinely trusted" },
      { id: "s6q6", type: "open", text: "What does leadership most consistently misunderstand about how your team's work actually functions?", sub: "This is one of the most important questions in this diagnostic. Be candid." },
    ]
  },
];

const TAILS = {
  executor: [
    { id: "rt1", type: "scale", text: "How often do you find unofficial workarounds because the official process doesn't work in practice?", lo: "Almost never", hi: "Daily — the workaround IS the process" },
    { id: "rt2", type: "open",  text: "What's the one thing nobody above you truly understands about what it takes to do this work?" },
    { id: "rt3", type: "radio", text: "If your team got a new tool tomorrow, what would actually change?",
      opts: ["A lot — the tool is genuinely the bottleneck", "Some things — there are real capability gaps", "Very little — the process problems would follow us", "Nothing — we've been here before with new tools"] },
  ],
  designer: [
    { id: "rt1", type: "scale", text: "How often does this process run the way you designed it to?", lo: "Almost never", hi: "Almost always" },
    { id: "rt2", type: "open",  text: "What assumption built into this process turned out to be wrong once people actually used it?" },
    { id: "rt3", type: "scale", text: "How frequently do you get honest, unfiltered feedback about how the process is actually experienced?", lo: "Almost never", hi: "Regularly and directly" },
  ],
  recipient: [
    { id: "rt1", type: "scale", text: "How often does what you receive match what you actually need — in quality, timing, and format?", lo: "Rarely — there's almost always a gap", hi: "Almost always spot on" },
    { id: "rt2", type: "open",  text: "What would you change about what you receive — the quality, format, timing, or level of completeness?" },
    { id: "rt3", type: "radio", text: "If the upstream process changed tomorrow, what would make the most difference to you?",
      opts: ["More consistent timing and reliability", "Better quality and completeness", "Clearer context and documentation", "Earlier heads-up before things land", "Two-way communication, not just handoffs"] },
  ],
  governor: [
    { id: "rt1", type: "scale", text: "How confident are you that your current view of team performance reflects the actual situation on the ground?", lo: "Not confident at all", hi: "Highly confident" },
    { id: "rt2", type: "open",  text: "What signal are you not currently getting that would most change how you lead or what you decide?" },
    { id: "rt3", type: "radio", text: "When you find out about a real problem, it's typically because:",
      opts: ["I heard it early and proactively", "A status update flagged it", "Someone escalated when it was already a crisis", "I noticed the symptoms before anyone told me", "I often don't find out until after the damage"] },
  ],
};

// ─── Styles ────────────────────────────────────────────────────────────────
const S = {
  page: { maxWidth: 680, margin: "0 auto", padding: "2.5rem 1.5rem 5rem", fontFamily: "'DM Sans', sans-serif" },
  header: { background: "#0d0d0d", padding: "1rem 2rem", display: "flex", alignItems: "center", justifyContent: "space-between" },
  brand: { fontFamily: "'DM Serif Display', serif", fontSize: 15, color: "#fff", letterSpacing: "0.02em" },
  brandAccent: { color: "#800080" },
  headerTag: { fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#778899" },
  progressWrap: { marginBottom: "2.5rem" },
  progressMeta: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#999", marginBottom: 8 },
  progressTrack: { height: 3, background: "#e8e8e8", borderRadius: 3, overflow: "hidden" },
  progressFill: (pct) => ({ height: 3, background: "#800080", borderRadius: 3, width: `${pct}%`, transition: "width 0.5s ease" }),
  chip: { display: "inline-block", fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase", background: "#f3e6f3", color: "#800080", padding: "3px 11px", borderRadius: 20, marginBottom: "1rem", fontWeight: 500 },
  accentBar: { width: 32, height: 3, background: "#008080", borderRadius: 2, marginBottom: "1rem" },
  h1: { fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.3rem)", lineHeight: 1.15, marginBottom: "0.75rem" },
  h1em: { color: "#800080", fontStyle: "italic" },
  h2: { fontFamily: "'DM Serif Display', serif", fontSize: "1.3rem", marginBottom: "0.4rem" },
  lead: { fontSize: 15, lineHeight: 1.7, color: "#555", marginBottom: "1rem" },
  callout: { borderLeft: "3px solid #008080", padding: "12px 16px", background: "#e6f3f3", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#004a4a", lineHeight: 1.6, marginBottom: "1.5rem" },
  qBlock: { marginBottom: "2.25rem" },
  qTitle: { fontFamily: "'DM Serif Display', serif", fontSize: "1.05rem", lineHeight: 1.3, color: "#1a1a1a", marginBottom: "0.3rem" },
  qSub: { fontSize: 13, color: "#666", lineHeight: 1.55, marginBottom: "0.85rem" },
  scaleRow: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "0.4rem" },
  scaleBtn: (sel) => ({ flex: 1, minWidth: 38, border: sel ? "none" : "0.5px solid #ddd", borderRadius: 8, padding: "10px 4px", fontSize: 13, fontWeight: 500, cursor: "pointer", background: sel ? "#800080" : "#fff", color: sel ? "#fff" : "#1a1a1a", textAlign: "center", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s" }),
  scaleEnds: { display: "flex", justifyContent: "space-between", fontSize: 11, color: "#999", marginBottom: "1.25rem", padding: "0 2px" },
  tileGrid: (n) => ({ display: "grid", gridTemplateColumns: `repeat(${Math.min(n, 5)}, minmax(0, 1fr))`, gap: 7, marginBottom: "1.25rem" }),
  tile: (sel) => ({ border: sel ? "none" : "0.5px solid #ddd", borderRadius: 10, padding: "10px 12px", cursor: "pointer", background: sel ? "#f3e6f3" : "#fff", textAlign: "center", transition: "all 0.15s", outline: sel ? "1.5px solid #800080" : "none" }),
  tileVal: (sel) => ({ display: "block", fontFamily: "'DM Serif Display', serif", fontSize: "1.05rem", color: sel ? "#800080" : "#1a1a1a", marginBottom: 2 }),
  tileLbl: (sel) => ({ fontSize: 11, color: sel ? "#800080" : "#999" }),
  choiceList: { display: "flex", flexDirection: "column", gap: 7, marginBottom: "1.25rem" },
  choiceItem: (sel, multi) => ({ display: "flex", alignItems: "center", gap: 11, border: sel ? "none" : "0.5px solid #ddd", outline: sel ? (multi ? "1.5px solid #008080" : "1.5px solid #800080") : "none", borderRadius: 10, padding: "11px 14px", cursor: "pointer", background: sel ? (multi ? "#e6f3f3" : "#f3e6f3") : "#fff", fontSize: 14, color: sel ? (multi ? "#004a4a" : "#800080") : "#1a1a1a", transition: "all 0.15s", userSelect: "none" }),
  radioCircle: (sel) => ({ width: 17, height: 17, border: sel ? "1.5px solid #800080" : "1.5px solid #bbb", borderRadius: "50%", flexShrink: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }),
  radioDot: { width: 7, height: 7, borderRadius: "50%", background: "#800080" },
  checkBox: (sel) => ({ width: 17, height: 17, border: sel ? "none" : "1.5px solid #bbb", borderRadius: 4, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, background: sel ? "#008080" : "transparent", color: "#fff" }),
  openField: { width: "100%", border: "0.5px solid #ddd", borderRadius: 10, padding: "12px 14px", fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: "#1a1a1a", background: "#fff", resize: "vertical", minHeight: 90, lineHeight: 1.6, marginBottom: "1.25rem" },
  roleGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginBottom: "1.5rem" },
  roleCard: (sel) => ({ border: sel ? "none" : "0.5px solid #ddd", outline: sel ? "1.5px solid #800080" : "none", borderRadius: 12, padding: "1rem 1.1rem", cursor: "pointer", background: sel ? "#f3e6f3" : "#fff", transition: "all 0.15s" }),
  rcName: (sel) => ({ fontSize: 14, fontWeight: 500, color: sel ? "#800080" : "#1a1a1a", marginBottom: 4 }),
  rcDesc: { fontSize: 12, color: "#999", lineHeight: 1.45 },
  navRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "0.5px solid #eee" },
  btnBack: { border: "0.5px solid #ddd", borderRadius: 8, padding: "10px 20px", fontSize: 13, cursor: "pointer", background: "transparent", color: "#666", fontFamily: "'DM Sans', sans-serif" },
  btnNext: (disabled) => ({ border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 500, cursor: disabled ? "default" : "pointer", background: disabled ? "#ccc" : "#800080", color: "#fff", fontFamily: "'DM Sans', sans-serif", opacity: disabled ? 0.5 : 1 }),
  skipLink: { fontSize: 12, color: "#999", cursor: "pointer", textDecoration: "underline" },
  inputField: { width: "100%", border: "0.5px solid #ddd", borderRadius: 10, padding: "11px 14px", fontSize: 14, fontFamily: "'DM Sans', sans-serif", color: "#1a1a1a", background: "#fff", marginBottom: "1rem", outline: "none" },
  label: { fontSize: 13, fontWeight: 500, color: "#1a1a1a", display: "block", marginBottom: "0.4rem" },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, margin: "1.25rem 0" },
  statBox: { background: "#f8f8f8", borderRadius: 8, padding: "0.9rem 1rem" },
  statVal: { fontFamily: "'DM Serif Display', serif", fontSize: "1.7rem", color: "#800080", lineHeight: 1 },
  statLbl: { fontSize: 11, color: "#999", marginTop: 5, lineHeight: 1.4 },
  insightFlag: (teal) => ({ borderLeft: `3px solid ${teal ? "#008080" : "#800080"}`, padding: "10px 14px", background: teal ? "#e6f3f3" : "#f3e6f3", borderRadius: "0 8px 8px 0", fontSize: 13, color: teal ? "#004a4a" : "#5a005a", lineHeight: 1.55, marginTop: 8 }),
  dimCard: { border: "0.5px solid #eee", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: 8, background: "#fff" },
  dimHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 },
  dimName: { fontSize: 13, fontWeight: 500, color: "#1a1a1a" },
  dimTag: { fontSize: 11, color: "#999" },
  barTrack: { height: 5, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" },
  barFill: (pct, color) => ({ height: 5, borderRadius: 3, width: `${pct}%`, background: color, transition: "width 1.2s ease" }),
  dimNote: { fontSize: 12, color: "#999", marginTop: 6 },
  ctaBlock: { background: "#0d0d0d", borderRadius: 14, padding: "1.75rem 2rem", marginTop: "2rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" },
  ctaText: { fontFamily: "'DM Serif Display', serif", fontSize: "1.2rem", color: "#f0f0f0", lineHeight: 1.3 },
  ctaSub: { fontSize: 12, color: "#778899", marginTop: 4 },
  ctaBtn: { border: "none", borderRadius: 30, padding: "11px 22px", background: "#800080", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", textDecoration: "none", display: "inline-block" },
  teamCodeBox: { background: "#f3e6f3", border: "1px solid #800080", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.5rem", textAlign: "center" },
  teamCodeLabel: { fontSize: 12, color: "#800080", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 },
  teamCodeValue: { fontFamily: "'DM Serif Display', serif", fontSize: "1.8rem", color: "#800080", letterSpacing: "0.08em" },
  teamCodeSub: { fontSize: 12, color: "#976b76", marginTop: 4 },
  shareBox: { background: "#e6f3f3", border: "0.5px solid #008080", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.5rem" },
  shareLabel: { fontSize: 12, color: "#008080", fontWeight: 500, marginBottom: 6 },
  shareUrl: { fontSize: 13, color: "#004a4a", wordBreak: "break-all", fontFamily: "monospace", background: "#fff", padding: "6px 10px", borderRadius: 6, border: "0.5px solid #b2d8d8" },
  eyebrow: { fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#999", fontWeight: 500, marginBottom: "0.6rem" },
  errorBox: { background: "#fff0f0", border: "0.5px solid #f09595", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#a32d2d", marginBottom: "1rem" },
};

// ─── Sub-components ────────────────────────────────────────────────────────
function ScaleQuestion({ q, value, onChange }) {
  return (
    <div style={S.qBlock}>
      <div style={S.qTitle}>{q.text}</div>
      {q.sub && <div style={S.qSub}>{q.sub}</div>}
      <div style={S.scaleRow}>
        {[1,2,3,4,5,6,7].map(v => (
          <button key={v} style={S.scaleBtn(value === v)} onClick={() => onChange(q.id, v)}>{v}</button>
        ))}
      </div>
      <div style={S.scaleEnds}><span>{q.lo}</span><span>{q.hi}</span></div>
    </div>
  );
}

function TilesQuestion({ q, value, onChange }) {
  return (
    <div style={S.qBlock}>
      <div style={S.qTitle}>{q.text}</div>
      {q.sub && <div style={S.qSub}>{q.sub}</div>}
      <div style={S.tileGrid(q.opts.length)}>
        {q.opts.map(o => (
          <div key={o.val} style={S.tile(value === o.val)} onClick={() => onChange(q.id, o.val)}>
            <span style={S.tileVal(value === o.val)}>{o.val}</span>
            <span style={S.tileLbl(value === o.val)}>{o.lbl}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RadioQuestion({ q, value, onChange }) {
  return (
    <div style={S.qBlock}>
      <div style={S.qTitle}>{q.text}</div>
      {q.sub && <div style={S.qSub}>{q.sub}</div>}
      <div style={S.choiceList}>
        {q.opts.map(o => (
          <div key={o} style={S.choiceItem(value === o, false)} onClick={() => onChange(q.id, o)}>
            <div style={S.radioCircle(value === o)}>{value === o && <div style={S.radioDot} />}</div>
            <span>{o}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiQuestion({ q, value = [], onChange }) {
  const toggle = (opt) => {
    const max = q.max || 99;
    if (value.includes(opt)) onChange(q.id, value.filter(v => v !== opt));
    else if (value.length < max) onChange(q.id, [...value, opt]);
  };
  return (
    <div style={S.qBlock}>
      <div style={S.qTitle}>{q.text}</div>
      {q.sub && <div style={S.qSub}>{q.sub}</div>}
      <div style={S.choiceList}>
        {q.opts.map(o => (
          <div key={o} style={S.choiceItem(value.includes(o), true)} onClick={() => toggle(o)}>
            <div style={S.checkBox(value.includes(o))}>{value.includes(o) ? "✓" : ""}</div>
            <span>{o}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OpenQuestion({ q, value = "", onChange }) {
  return (
    <div style={S.qBlock}>
      <div style={S.qTitle}>{q.text}</div>
      {q.sub && <div style={S.qSub}>{q.sub}</div>}
      <textarea style={S.openField} placeholder="Your thoughts here..." value={value} onChange={e => onChange(q.id, e.target.value)} />
    </div>
  );
}

function Question({ q, value, onChange }) {
  if (q.type === "scale") return <ScaleQuestion q={q} value={value} onChange={onChange} />;
  if (q.type === "tiles") return <TilesQuestion q={q} value={value} onChange={onChange} />;
  if (q.type === "radio") return <RadioQuestion q={q} value={value} onChange={onChange} />;
  if (q.type === "multi") return <MultiQuestion q={q} value={value} onChange={onChange} />;
  if (q.type === "open")  return <OpenQuestion  q={q} value={value} onChange={onChange} />;
  return null;
}

// ─── Results helpers ───────────────────────────────────────────────────────
const toolMap  = { "1–2": 1.5, "3–5": 4, "6–8": 7, "9–12": 10, "12+": 14 };
const ctxMap   = { "Under 30 min": 15, "30–60 min": 45, "1–2 hours": 90, "2–3 hours": 150, "3+ hours": 210 };
const meetMap  = { "Under 3 hrs": 2, "3–6 hrs": 4.5, "6–10 hrs": 8, "10–15 hrs": 12, "15+ hrs": 18 };
const rptMap   = { "Under 1 hr": 0.5, "1–3 hrs": 2, "3–5 hrs": 4, "5–8 hrs": 6.5, "8+ hrs": 10 };
const priMap   = { "0": 0, "1–2": 1.5, "3–5": 4, "6–10": 8, "10+": 12 };
const capMap   = { "0–25%": 12, "25–50%": 37, "50–75%": 62, "75–90%": 82, "90%+": 95 };

function scalePct(ans, keys, inv = []) {
  let t = 0, c = 0;
  keys.forEach((k, i) => {
    const v = typeof ans[k] === "number" ? (inv.includes(i) ? 8 - ans[k] : ans[k]) : null;
    if (v !== null) { t += v; c++; }
  });
  return c > 0 ? Math.round((t / c / 7) * 100) : null;
}

// ─── Dimension interpretations ─────────────────────────────────────────────
function getDimInterpretation(dimId, pct, ans) {
  const priFlips  = priMap[ans.s2q2]  !== undefined ? priMap[ans.s2q2] : null;
  const capPct    = capMap[ans.s3q3]  || null;
  const toolCount = toolMap[ans.s4q1] || null;
  const rptHrs    = rptMap[ans.s6q2]  || null;
  const ctxMin    = ctxMap[ans.s4q2]  || null;
  const meetHrs   = meetMap[ans.s4q3] || null;

  const interps = {
    s1: {
      green: {
        pattern: "Work arrives with enough context for your team to act on it effectively.",
        nudge: "Protect this by documenting how work enters — what's informal now can break when people or priorities change.",
      },
      amber: {
        pattern: "Work is reaching your team, but context gaps are creating friction at the start of every cycle.",
        callout: ans.s1q1 === "It materializes with no clear origin" ? "You noted that work often arrives with no clear origin — that ambiguity compounds quickly." : ans.s1q2 ? `Work typically arrives with only ${ans.s1q2} of lead time, which limits your team's ability to plan.` : null,
        nudge: "Establish a lightweight intake ritual — even a single required field (who requested this, and why it matters now) reduces downstream confusion significantly.",
      },
      red: {
        pattern: "Your team is absorbing work reactively, without the context needed to do it well.",
        callout: ans.s1q1 === "Top-down directive — leadership decides and assigns" ? "Work arrives as directives — which means context is being filtered before it reaches the people doing the work." : "The way work arrives is generating friction before execution even begins.",
        nudge: "Start by mapping how work actually enters — not how it's supposed to. The gap between those two pictures is usually where the problem lives.",
      },
    },
    s2: {
      green: {
        pattern: "Your team has a shared understanding of what matters most and trusts the decisions behind it.",
        nudge: "Revisit your prioritization criteria quarterly — what works now may not hold as the team or org scales.",
      },
      amber: {
        pattern: "Prioritization is happening, but the logic isn't consistently visible or trusted by the people doing the work.",
        callout: priFlips !== null && priFlips >= 4 ? `Priorities shifted approximately ${priFlips} times in the last 30 days — that frequency makes it nearly impossible to build momentum.` : ans.s2q1 === "Who escalates the loudest" ? "You indicated that escalation volume drives priority decisions more than deliberate criteria." : null,
        nudge: "A visible, shared priority stack — even a simple ranked list — reduces the noise. The goal isn't rigidity, it's transparency about trade-offs.",
      },
      red: {
        pattern: "Priority decisions are being made in ways your team can't see or trust, which erodes both momentum and morale.",
        callout: priFlips !== null && priFlips >= 6 ? `With approximately ${priFlips} priority shifts in 30 days, your team is likely spending more energy reorienting than executing.` : "The absence of a clear prioritization framework means every decision is a negotiation — and the loudest voice usually wins.",
        nudge: "Before anything else, name your top three priorities explicitly and share them with the team. Alignment starts with visibility.",
      },
    },
    s3: {
      green: {
        pattern: "Work is reaching the right people and your team's capacity is being managed thoughtfully.",
        nudge: "Codify how assignment decisions get made — institutional knowledge about who takes what tends to disappear when key people leave.",
      },
      amber: {
        pattern: "Work is getting assigned, but the signals driving those decisions don't fully account for capacity or fit.",
        callout: capPct !== null && capPct >= 75 ? `You estimate ${capPct}% of your team is at or near capacity — which means most assignment decisions are being made with very little room for error.` : ans.s3q1 && ans.s3q1.includes("Who's been vocal or present lately") ? "Assignments are partly driven by visibility rather than skill or bandwidth — which quietly disadvantages quieter team members." : null,
        nudge: "A simple weekly capacity check-in — even informal — creates the shared awareness needed to make better assignment decisions.",
      },
      red: {
        pattern: "Your team is absorbing work without a reliable way to match it to the right people at the right time.",
        callout: capPct !== null && capPct >= 90 ? `With ${capPct}% of the team at or near capacity, you're one unexpected priority from a real breakdown.` : ans.s3q4 === "They absorb it and struggle quietly" ? "When people are overloaded, the default is to absorb it quietly — which means problems stay invisible until they're already costly." : null,
        nudge: "Make capacity visible before it becomes a crisis. Even a basic signal — green, yellow, red per person — changes the conversation.",
      },
    },
    s4: {
      green: {
        pattern: "Your team has the conditions it needs to do focused, effective work.",
        nudge: "Audit your tool stack annually — tools that made sense at one stage of growth often become friction at the next.",
      },
      amber: {
        pattern: "Execution is happening, but structural friction is costing your team time and focus they can't afford to lose.",
        callout: toolCount !== null && toolCount >= 6 ? `Your team works across approximately ${toolCount} tools — that many context shifts per day adds up to significant cognitive overhead.` : ctxMin !== null && ctxMin >= 90 ? `You estimated losing around ${ctxMin} minutes per day to context-switching — that's nearly ${Math.round(ctxMin * 5 / 60)} hours a week that isn't going toward the work itself.` : null,
        nudge: "Identify the one friction point your team complains about most and eliminate it completely, rather than optimizing around it.",
      },
      red: {
        pattern: "The conditions for effective execution are significantly compromised — your team is working hard against the process, not with it.",
        callout: meetHrs !== null && meetHrs >= 10 ? `At approximately ${meetHrs} hours of meetings per week, your team has limited uninterrupted time to actually do the work they're meeting about.` : toolCount !== null && toolCount >= 9 ? `Working across ${toolCount}+ tools means information is fragmented by design — and finding what you need takes time that should go toward the work.` : null,
        nudge: "Start with a single question: what would your team get done if one meeting, one approval step, or one tool disappeared tomorrow? That answer usually points to the highest-leverage change.",
      },
    },
    s5: {
      green: {
        pattern: "Your team can raise problems and get help without it becoming a political event.",
        nudge: "Psychological safety is fragile — it requires consistent modeling from leadership. Name it explicitly so the team knows it's intentional, not accidental.",
      },
      amber: {
        pattern: "Support exists in theory, but the path to getting it isn't reliable or fast enough to prevent blockers from compounding.",
        callout: ans.s5q3 !== undefined && ans.s5q3 <= 3 ? "Your team doesn't fully trust that raising problems early is safe — which means issues are likely being held longer than they should be." : ans.s5q2 ? `Blockers typically take ${ans.s5q2} to resolve — that lag has a compounding effect on everything downstream.` : null,
        nudge: "Create one predictable, low-stakes channel for flagging blockers early — a weekly 'what's stuck' ritual costs almost nothing and surfaces issues before they become crises.",
      },
      red: {
        pattern: "Your team is navigating blockers largely on their own, and the cost of asking for help may feel higher than the cost of struggling through.",
        callout: ans.s5q4 !== undefined && ans.s5q4 <= 2 ? "When problems get raised, action rarely follows — which teaches people that flagging issues isn't worth the exposure." : ans.s5q3 !== undefined && ans.s5q3 <= 2 ? "The team doesn't feel safe raising problems early — a pattern that turns small issues into expensive ones." : null,
        nudge: "The fastest way to rebuild support culture is for leadership to visibly act on the next thing that gets flagged — one clear response changes the calculus for everyone watching.",
      },
    },
    s6: {
      green: {
        pattern: "Leadership has an accurate picture of the work, and reporting serves the team rather than performing for it.",
        nudge: "Formalize what's working — document the reporting rhythm and format so it survives leadership changes.",
      },
      amber: {
        pattern: "Reporting is happening, but it's consuming more team energy than the insight it produces is worth.",
        callout: rptHrs !== null && rptHrs >= 3 ? `Your team spends approximately ${rptHrs} hours per week on status updates and reports — time that could otherwise go toward the work itself.` : ans.s6q1 && ans.s6q1.includes("A slide deck or PowerPoint presentation") ? "Leadership is still requesting slide decks as the primary update format — a format that optimizes for presentation over accuracy." : null,
        nudge: "Propose one change to the reporting format and measure whether decision quality improves. Reporting should inform decisions, not replace them.",
      },
      red: {
        pattern: "There's a significant gap between what leadership believes about the work and what the team actually experiences — and current reporting isn't closing it.",
        callout: ans.s6q3 === "Expects everything to fit their existing mental model" ? "Leadership's openness to new approaches is limited — which means your team is adapting to leadership's preferences rather than the other way around." : ans.s6q3 === "Signals — explicitly or subtly — not to rock the boat" ? "The signal your team is getting is to stay in their lane — which keeps real problems invisible." : rptHrs !== null && rptHrs >= 5 ? `At ${rptHrs} hours per week on reporting, the overhead of making work visible is itself becoming a drag on the work.` : null,
        nudge: "Find one metric leadership currently tracks that doesn't reflect team reality, and replace it with one that does. Changing what gets measured changes what gets seen.",
      },
    },
  };

  const band = pct >= 70 ? "green" : pct >= 45 ? "amber" : "red";
  return interps[dimId]?.[band] || null;
}

// ─── Main component ────────────────────────────────────────────────────────
export default function Process360Diagnostic() {
  const FLOW = ["gate", "intro", "role", ...SECTIONS.map(s => s.id), "tail", "results"];
  const hasTeamCode = !!new URLSearchParams(window.location.search).get("team");
  const [stepIdx, setStepIdx] = useState(hasTeamCode ? 1 : 0);
  const [gate, setGate]       = useState({ name: "", email: "", org: "" });
  const [gateError, setGateError] = useState("");
  const [teamCode, setTeamCode]   = useState(new URLSearchParams(window.location.search).get("team") || "");
  const [role, setRole]           = useState(null);
  const [ans, setAns]             = useState({});
  const [roleAns, setRoleAns]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const step = FLOW[stepIdx];
  const pct  = Math.round((stepIdx / (FLOW.length - 1)) * 100);

  const setAnswer    = (id, val) => setAns(prev => ({ ...prev, [id]: val }));
  const setRoleAnswer = (id, val) => setRoleAns(prev => ({ ...prev, [id]: val }));

  const go  = (d) => { setStepIdx(i => Math.max(0, Math.min(FLOW.length - 1, i + d))); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const next = () => go(1);
  const back = () => go(-1);

  // Gate submit
  const handleGateSubmit = () => {
    if (!gate.name.trim() || !gate.email.trim() || !gate.org.trim()) {
      setGateError("Please fill in all three fields to continue.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(gate.email)) {
      setGateError("Please enter a valid email address.");
      return;
    }
    setGateError("");
    const code = generateTeamCode(gate.org);
    setTeamCode(code);
    next();
  };

  // Final submit to Supabase
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    const tailQs = TAILS[role] || [];
    const payload = {
      team_code: teamCode,
      respondent_name: gate.name,
      respondent_email: gate.email,
      org_name: gate.org,
      role,
      s1q1: ans.s1q1 || null, s1q2: ans.s1q2 || null, s1q3: ans.s1q3 || null, s1q4: ans.s1q4 || null, s1q5: ans.s1q5 || null,
      s2q1: ans.s2q1 || null, s2q2: ans.s2q2 || null, s2q3: ans.s2q3 || null, s2q4: ans.s2q4 || null, s2q5: ans.s2q5 || null,
      s3q1: Array.isArray(ans.s3q1) ? ans.s3q1.join("; ") : (ans.s3q1 || null),
      s3q2: ans.s3q2 || null, s3q3: ans.s3q3 || null, s3q4: ans.s3q4 || null, s3q5: ans.s3q5 || null,
      s4q1: ans.s4q1 || null, s4q2: ans.s4q2 || null, s4q3: ans.s4q3 || null, s4q4: ans.s4q4 || null,
      s4q5: Array.isArray(ans.s4q5) ? ans.s4q5.join("; ") : (ans.s4q5 || null),
      s4q6: ans.s4q6 || null,
      s5q1: ans.s5q1 || null, s5q2: ans.s5q2 || null, s5q3: ans.s5q3 || null, s5q4: ans.s5q4 || null, s5q5: ans.s5q5 || null,
      s6q1: Array.isArray(ans.s6q1) ? ans.s6q1.join("; ") : (ans.s6q1 || null),
      s6q2: ans.s6q2 || null, s6q3: ans.s6q3 || null, s6q4: ans.s6q4 || null, s6q5: ans.s6q5 || null, s6q6: ans.s6q6 || null,
      rt1: roleAns[tailQs[0]?.id] || null,
      rt2: roleAns[tailQs[1]?.id] || null,
      rt3: typeof roleAns[tailQs[2]?.id] === "number" ? roleAns[tailQs[2]?.id] : (roleAns[tailQs[2]?.id] || null),
    };
    const { error } = await supabase.from("responses").insert([payload]);
    setSubmitting(false);
    if (error) { setSubmitError("Something went wrong saving your response. Please try again."); return; }
    next();
  };

  // ── Screens ──────────────────────────────────────────────────────────────

  // Gate screen
  if (step === "gate") return (
    <div>
      <header style={S.header}>
        <span style={S.brand}>Alter <span style={S.brandAccent}>EQ</span></span>
        <span style={S.headerTag}>Process 360 Diagnostic</span>
      </header>
      <div style={S.page}>
        <div style={S.accentBar} />
        <h1 style={S.h1}>Your process, through <em style={S.h1em}>every</em> lens.</h1>
        <p style={S.lead}>Most process reviews ask one group of people what they think. This diagnostic captures how the same process looks from four different vantage points — and finds the gaps that no single perspective can see alone.</p>
        <div style={S.callout}>Before you begin: bring one specific process to mind — something your team runs regularly. Hold it in focus as you respond. Your answers are most useful when grounded in something concrete.</div>
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={S.label}>Your name</label>
          <input style={S.inputField} type="text" placeholder="Alexis Felton" value={gate.name} onChange={e => setGate(g => ({ ...g, name: e.target.value }))} />
          <label style={S.label}>Work email</label>
          <input style={S.inputField} type="email" placeholder="alexis@altereq.com" value={gate.email} onChange={e => setGate(g => ({ ...g, email: e.target.value }))} />
          <label style={S.label}>Organization name</label>
          <input style={S.inputField} type="text" placeholder="Alter EQ" value={gate.org} onChange={e => setGate(g => ({ ...g, org: e.target.value }))} />
        </div>
        {gateError && <div style={S.errorBox}>{gateError}</div>}
        <div style={{ ...S.navRow, borderTop: "none", paddingTop: 0, justifyContent: "flex-end" }}>
          <button style={S.btnNext(false)} onClick={handleGateSubmit}>Begin the diagnostic →</button>
        </div>
        <p style={{ fontSize: 12, color: "#999", marginTop: "1rem" }}>Your information is confidential and will only be used to aggregate responses from your organization.</p>
      </div>
    </div>
  );

  // Intro screen
  if (step === "intro") return (
    <div>
      <header style={S.header}>
        <span style={S.brand}>Alter <span style={S.brandAccent}>EQ</span></span>
        <span style={S.headerTag}>Process 360 Diagnostic</span>
      </header>
      <div style={S.page}>
        <div style={{ ...S.progressWrap }}>
          <div style={S.progressMeta}><span style={{ color: "#800080", fontWeight: 500 }}>Welcome, {gate.name.split(" ")[0]}</span><span>{pct}% complete</span></div>
          <div style={S.progressTrack}><div style={S.progressFill(pct)} /></div>
        </div>
        <div style={S.teamCodeBox}>
          <div style={S.teamCodeLabel}>Your team code</div>
          <div style={S.teamCodeValue}>{teamCode}</div>
          <div style={S.teamCodeSub}>Share this with teammates so their responses are grouped with yours</div>
        </div>
        <div style={S.shareBox}>
          <div style={S.shareLabel}>Shareable team link</div>
          <div style={S.shareUrl}>{window.location.origin}{window.location.pathname}?team={teamCode}</div>
        </div>
        <p style={S.lead}>You'll move through 6 dimensions of how work flows through your team, followed by a short set of questions specific to your role. Takes about 15–20 minutes.</p>
        <div style={{ ...S.navRow, borderTop: "none", paddingTop: 0, justifyContent: "flex-end" }}>
          <button style={S.btnNext(false)} onClick={next}>Start the diagnostic →</button>
        </div>
      </div>
    </div>
  );

  // Role screen
  if (step === "role") return (
    <div>
      <header style={S.header}>
        <span style={S.brand}>Alter <span style={S.brandAccent}>EQ</span></span>
        <span style={S.headerTag}>Process 360 Diagnostic</span>
      </header>
      <div style={S.page}>
        <div style={S.progressWrap}>
          <div style={S.progressMeta}><span style={{ color: "#800080", fontWeight: 500 }}>Your vantage point</span><span>{pct}% complete</span></div>
          <div style={S.progressTrack}><div style={S.progressFill(pct)} /></div>
        </div>
        <span style={S.chip}>Your vantage point</span>
        <h2 style={S.h2}>Where do you sit relative to this process?</h2>
        <p style={{ ...S.qSub, marginBottom: "1.25rem" }}>This shapes a short set of additional questions at the end. The core diagnostic is the same for everyone.</p>
        <div style={S.roleGrid}>
          {ROLES.map(r => (
            <div key={r.id} style={S.roleCard(role === r.id)} onClick={() => setRole(r.id)}>
              <div style={S.rcName(role === r.id)}>{r.name}</div>
              <div style={S.rcDesc}>{r.desc}</div>
            </div>
          ))}
        </div>
        <div style={S.navRow}>
          <button style={S.btnBack} onClick={back}>Back</button>
          <button style={S.btnNext(!role)} disabled={!role} onClick={next}>Continue →</button>
        </div>
      </div>
    </div>
  );

  // Section screens
  const sectionData = SECTIONS.find(s => s.id === step);
  if (sectionData) return (
    <div>
      <header style={S.header}>
        <span style={S.brand}>Alter <span style={S.brandAccent}>EQ</span></span>
        <span style={S.headerTag}>Process 360 Diagnostic</span>
      </header>
      <div style={S.page}>
        <div style={S.progressWrap}>
          <div style={S.progressMeta}><span style={{ color: "#800080", fontWeight: 500 }}>Section {sectionData.sectionNum} of {sectionData.total}</span><span>{pct}% complete</span></div>
          <div style={S.progressTrack}><div style={S.progressFill(pct)} /></div>
        </div>
        <span style={S.chip}>{sectionData.section}</span>
        {sectionData.questions.map(q => (
          <Question key={q.id} q={q} value={ans[q.id]} onChange={setAnswer} />
        ))}
        <div style={S.navRow}>
          <button style={S.btnBack} onClick={back}>Back</button>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={S.skipLink} onClick={next}>Skip section</span>
            <button style={S.btnNext(false)} onClick={next}>Continue →</button>
          </div>
        </div>
      </div>
    </div>
  );

  // Role tail
  if (step === "tail") {
    const roleData = ROLES.find(r => r.id === role) || ROLES[0];
    const tailQs   = TAILS[role] || TAILS.executor;
    return (
      <div>
        <header style={S.header}>
          <span style={S.brand}>Alter <span style={S.brandAccent}>EQ</span></span>
          <span style={S.headerTag}>Process 360 Diagnostic</span>
        </header>
        <div style={S.page}>
          <div style={S.progressWrap}>
            <div style={S.progressMeta}><span style={{ color: "#800080", fontWeight: 500 }}>Your perspective</span><span>{pct}% complete</span></div>
            <div style={S.progressTrack}><div style={S.progressFill(pct)} /></div>
          </div>
          <span style={S.chip}>As {roleData.name}</span>
          <h2 style={S.h2}>A few questions only you can answer.</h2>
          <p style={{ ...S.qSub, marginBottom: "1.5rem" }}>These surface what's only visible from your vantage point.</p>
          {tailQs.map(q => (
            <Question key={q.id} q={q} value={roleAns[q.id]} onChange={setRoleAnswer} />
          ))}
          {submitError && <div style={S.errorBox}>{submitError}</div>}
          <div style={S.navRow}>
            <button style={S.btnBack} onClick={back}>Back</button>
            <button style={S.btnNext(submitting)} disabled={submitting} onClick={handleSubmit}>
              {submitting ? "Saving..." : "See your readout →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (step === "results") {
    const toolCount = toolMap[ans.s4q1] || null;
    const ctxMin    = ctxMap[ans.s4q2]  || null;
    const meetHrs   = meetMap[ans.s4q3] || null;
    const rptHrs    = rptMap[ans.s6q2]  || null;
    const priFlips  = priMap[ans.s2q2] !== undefined ? priMap[ans.s2q2] : null;
    const capPct    = capMap[ans.s3q3]  || null;

    const dims = [
      { id: "s1", name: "How work finds you",         pct: scalePct(ans, ["s1q3"]) },
      { id: "s2", name: "How work gets weighed",       pct: scalePct(ans, ["s2q3", "s2q4"]) },
      { id: "s3", name: "How work gets to people",     pct: scalePct(ans, ["s3q2", "s3q5"], [1]) },
      { id: "s4", name: "How work actually gets done", pct: scalePct(ans, ["s4q4"]) },
      { id: "s5", name: "How teams get support",       pct: scalePct(ans, ["s5q1", "s5q3", "s5q4"]) },
      { id: "s6", name: "How work gets seen",          pct: scalePct(ans, ["s6q4", "s6q5"]) },
    ].filter(d => d.pct !== null);

    const ragColor  = (pct) => pct >= 70 ? "#16a34a" : pct >= 45 ? "#d97706" : "#dc2626";
    const ragBg     = (pct) => pct >= 70 ? "#f0fdf4" : pct >= 45 ? "#fffbeb" : "#fef2f2";
    const ragBorder = (pct) => pct >= 70 ? "#bbf7d0" : pct >= 45 ? "#fde68a" : "#fecaca";
    const ragLabel  = (pct) => pct >= 70 ? "Effective" : pct >= 45 ? "Showing strain" : "Needs attention";

    const hasStats = toolCount || rptHrs || meetHrs || priFlips !== null || capPct || ctxMin;

    return (
      <div>
        <header style={S.header}>
          <span style={S.brand}>Alter <span style={S.brandAccent}>EQ</span></span>
          <span style={S.headerTag}>Process 360 Diagnostic</span>
        </header>
        <div style={S.page}>
          <div style={S.eyebrow}>Your Process 360 readout</div>
          <h1 style={{ ...S.h1, fontSize: "1.8rem", marginBottom: "0.5rem" }}>Here's what your responses reveal.</h1>
          <p style={{ fontSize: 14, color: "#555", lineHeight: 1.65, marginBottom: "0.5rem" }}>
            This is your individual perspective. The scores below reflect how <em>you</em> experience this process — not how the process performs overall.
          </p>
          <p style={{ fontSize: 14, color: "#555", lineHeight: 1.65, marginBottom: "1.5rem" }}>
            The full picture — where perspectives align, diverge, and contradict — emerges when all four vantage points are placed side by side.
          </p>

          {hasStats && <>
            <div style={S.eyebrow}>By the numbers</div>
            <div style={S.statGrid}>
              {toolCount   && <div style={S.statBox}><div style={S.statVal}>~{toolCount}</div><div style={S.statLbl}>tools in your team's daily stack</div></div>}
              {rptHrs      && <div style={S.statBox}><div style={S.statVal}>{rptHrs}h</div><div style={S.statLbl}>per week on updates &amp; reports</div></div>}
              {meetHrs     && <div style={S.statBox}><div style={S.statVal}>{meetHrs}h</div><div style={S.statLbl}>per week in meetings</div></div>}
              {priFlips !== null && <div style={S.statBox}><div style={S.statVal}>{priFlips}x</div><div style={S.statLbl}>priority shifts last 30 days</div></div>}
              {capPct      && <div style={S.statBox}><div style={S.statVal}>{capPct}%</div><div style={S.statLbl}>of team at or near capacity</div></div>}
              {ctxMin      && <div style={S.statBox}><div style={S.statVal}>{ctxMin}m</div><div style={S.statLbl}>per day lost to context-switching</div></div>}
            </div>
          </>}

          <div style={{ ...S.eyebrow, marginTop: "1.75rem" }}>Dimension assessment</div>
          <p style={{ fontSize: 12, color: "#999", marginBottom: "1.25rem", lineHeight: 1.5 }}>
            Scores reflect effectiveness based on your responses. 70%+ = Effective &nbsp;·&nbsp; 45–69% = Showing strain &nbsp;·&nbsp; Below 45% = Needs attention
          </p>

          {dims.map(d => {
            const interp = getDimInterpretation(d.id, d.pct, ans);
            return (
              <div key={d.id} style={{ ...S.dimCard, borderColor: ragBorder(d.pct), background: "#fff", marginBottom: 12 }}>
                <div style={S.dimHead}>
                  <div style={S.dimName}>{d.name}</div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: ragColor(d.pct), background: ragBg(d.pct), border: `1px solid ${ragBorder(d.pct)}`, padding: "2px 10px", borderRadius: 20 }}>{ragLabel(d.pct)}</div>
                </div>
                <div style={S.barTrack}>
                  <div style={{ ...S.barFill(d.pct, ragColor(d.pct)) }} />
                </div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 5, marginBottom: interp ? 12 : 0 }}>{d.pct}% effectiveness score</div>
                {interp && <>
                  <div style={{ fontSize: 13, color: "#1a1a1a", lineHeight: 1.6, fontStyle: "italic", marginBottom: 8, paddingTop: 8, borderTop: "0.5px solid #f0f0f0" }}>
                    "{interp.pattern}"
                  </div>
                  {interp.callout && (
                    <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6, background: "#f8f8f8", borderRadius: 6, padding: "7px 10px", marginBottom: 8 }}>
                      <strong style={{ fontWeight: 500, color: "#1a1a1a" }}>What we noticed: </strong>{interp.callout}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#004a4a", lineHeight: 1.6, background: "#e6f3f3", borderRadius: 6, padding: "7px 10px", borderLeft: "3px solid #008080" }}>
                    <strong style={{ fontWeight: 500 }}>One thing to consider: </strong>{interp.nudge}
                  </div>
                </>}
              </div>
            );
          })}

          <div style={{ background: "#f8f8f8", borderRadius: 12, padding: "1.25rem 1.5rem", marginTop: "1.5rem", marginBottom: "1.5rem" }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#1a1a1a", marginBottom: 6 }}>What comes next</div>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7, margin: 0 }}>
              This readout reflects one vantage point. The Process 360 is designed to surface the gaps between how the same process looks to the people who design it, the people who execute it, the people who receive its output, and the people who govern it. Those gaps — not the individual scores — are where the real insight lives. Share the link below with teammates to add their perspective.
            </p>
            {teamCode && (
              <div style={{ marginTop: 12, background: "#fff", border: "0.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#555" }}>
                <strong style={{ fontWeight: 500, color: "#1a1a1a" }}>Your team link: </strong>
                <span style={{ fontFamily: "monospace", color: "#800080" }}>{window.location.origin}{window.location.pathname}?team={teamCode}</span>
              </div>
            )}
          </div>

          <div style={S.ctaBlock}>
            <div>
              <div style={S.ctaText}>Ready to see all four perspectives side by side?</div>
              <div style={S.ctaSub}>The gap analysis is where the real insight lives.</div>
            </div>
            <a style={S.ctaBtn} href="https://www.altereq.com/contact" target="_blank" rel="noreferrer">Talk to Alexis →</a>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
